<script module lang="ts">
  import type { Snippet } from 'svelte';
  import { type AllowedState } from './states.types';
  import { type AllowedAlign } from './button.types';

  export interface Props {
    state?: AllowedState;
    align?: AllowedAlign;
    href?: string;
    onclick?: () => void;
    disabled?: boolean;
    /** Rückfrage vor dem Auslösen, zum Beispiel "Sind Sie sicher?". */
    question?: string;
    /** Sekunden, die gedrückt werden muss, bevor der Button von selbst auslöst. */
    hold?: number;
    children?: Snippet;
  }
</script>

<script lang="ts">
  import { allowedStates } from './states.types';
  import { allowedAligns } from './button.types';
  import { PointService } from '../points/PointService';

  // state wird beim Auspacken umbenannt: eine lokale Bindung namens "state" würde sonst mit
  // der $state-Rune kollidieren (store_rune_conflict). Nach aussen bleibt der Name wie bei Box.
  const { state: colorState, align, href, onclick, disabled, question, hold, children }: Props = $props();

  // -
  const DefaultState: AllowedState = 'brand';
  const DisabledState = 'disabled';
  const DefaultAlign = allowedAligns[0];
  const Align = $derived(align && allowedAligns.includes(align) ? align : DefaultAlign);

  // -
  const Point = new PointService();
  const Link = $derived(Point.buildLink(href));

  // - Ohne href und ohne onclick gibt es nichts auszulösen, dann ist der Button abgeschaltet.
  const IsDisabled = $derived(disabled === true || (!Link.hasLink && onclick === undefined));
  const ElementTag = $derived(IsDisabled ? 'span' : Link.hasLink ? 'a' : 'button');
  const UseState = $derived(IsDisabled ? DisabledState : colorState && allowedStates.includes(colorState) ? colorState : DefaultState);

  // -
  const HintSeconds = 2;
  const HoldSeconds = $derived(Math.max(0, hold ?? 0));
  const NeedsHold = $derived(HoldSeconds > 0);
  const HoldHint = $derived(`${HoldSeconds.toString()} s gedrückt halten`);
  const Question = $derived((question ?? '').trim());
  const NeedsQuestion = $derived(Question !== '');
  const NeedsGuard = $derived(NeedsHold || NeedsQuestion);

  // -
  let Container: HTMLElement | undefined = $state();
  let IsHolding = $state(false);
  let IsHeld = $state(false);
  let IsAsking = $state(false);
  let ShowHint = $state(false);
  let HoldTimer: number | undefined;
  let HintTimer: number | undefined;

  /** Der Klick, der zum Loslassen nach erfüllter Haltezeit gehört, ist bereits verbraucht. */
  let SkipNextClick = false;

  function run(): void {
    const Url = Link.url;
    const LinkTarget = Link.target;

    reset();
    onclick?.();

    if (!Link.hasLink) {
      return;
    }

    // Ausgelöst wird ohne Klickereignis, also muss der Sprung selbst angestossen werden.
    if (LinkTarget === '_blank') {
      window.open(Url, LinkTarget, 'noopener,noreferrer');
      return;
    }

    window.location.assign(Url);
  }

  function startHold(): void {
    if (IsDisabled || !NeedsHold || IsHeld || IsHolding) {
      return;
    }

    SkipNextClick = false;
    IsHolding = true;
    HoldTimer = window.setTimeout(() => {
      HoldTimer = undefined;
      IsHolding = false;
      IsHeld = true;
      SkipNextClick = true;

      // Die Rückfrage kommt erst, nachdem die Haltezeit erfüllt ist.
      if (NeedsQuestion) {
        IsAsking = true;
        return;
      }

      run();
    }, HoldSeconds * 1000);
  }

  /** Beendet nur das Drücken - eine bereits erreichte Haltezeit bleibt bestehen. */
  function cancelHold(): void {
    if (HoldTimer !== undefined) {
      window.clearTimeout(HoldTimer);
      HoldTimer = undefined;
    }

    IsHolding = false;
  }

  function reset(): void {
    cancelHold();
    IsHeld = false;
    IsAsking = false;
  }

  /** Zu kurz gedrückt: sonst bliebe unklar, warum nichts passiert. */
  function showHint(): void {
    if (HintTimer !== undefined) {
      window.clearTimeout(HintTimer);
    }

    ShowHint = true;
    HintTimer = window.setTimeout(() => {
      HintTimer = undefined;
      ShowHint = false;
    }, HintSeconds * 1000);
  }

  function handleClick(event: MouseEvent): void {
    if (IsDisabled) {
      event.preventDefault();
      return;
    }

    if (!NeedsGuard) {
      onclick?.();
      return;
    }

    event.preventDefault();

    if (SkipNextClick) {
      SkipNextClick = false;
      return;
    }

    if (IsHolding) {
      return;
    }

    if (NeedsHold && !IsHeld) {
      showHint();
      return;
    }

    if (NeedsQuestion && !IsAsking) {
      IsAsking = true;
      return;
    }

    run();
  }

  function handleKeyDown(event: KeyboardEvent): void {
    if (IsDisabled || !NeedsHold || (event.key !== 'Enter' && event.key !== ' ')) {
      return;
    }

    event.preventDefault();

    if (!event.repeat) {
      startHold();
    }
  }

  function handleKeyUp(event: KeyboardEvent): void {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    if (IsHolding) {
      showHint();
    }

    cancelHold();
  }

  function handlePointerLeave(): void {
    if (IsHolding) {
      showHint();
    }

    cancelHold();
    SkipNextClick = false;

    if (!IsAsking) {
      IsHeld = false;
    }
  }

  function cancelOnOutside(event: PointerEvent): void {
    const Target = event.target;

    if ((!IsAsking && !IsHeld) || (Target instanceof Node && Container?.contains(Target) === true)) {
      return;
    }

    reset();
  }

  function cancelOnEscape(event: KeyboardEvent): void {
    if ((!IsAsking && !IsHeld) || event.key !== 'Escape') {
      return;
    }

    reset();
  }

  $effect(() => {
    return (): void => {
      cancelHold();

      if (HintTimer !== undefined) {
        window.clearTimeout(HintTimer);
        HintTimer = undefined;
      }
    };
  });
</script>

<svelte:window onpointerdown={cancelOnOutside} onkeydown={cancelOnEscape} />

<svelte:element
  this={ElementTag}
  bind:this={Container}
  class="tagButton {Align === 'left' ? 'alignLeft' : ''} {IsHolding ? 'holding' : ''} {IsHeld ? 'held' : ''} {IsAsking ? 'asking' : ''} {ShowHint ? 'hinting' : ''}"
  role={Link.hasLink && !IsDisabled ? 'link' : 'button'}
  type={ElementTag === 'button' ? 'button' : undefined}
  href={Link.hasLink && !IsDisabled ? Link.url : undefined}
  target={IsDisabled ? undefined : Link.target}
  rel={IsDisabled ? undefined : Link.rel}
  aria-disabled={IsDisabled ? 'true' : undefined}
  style:--button-color={`var(--color-${UseState}-dominant)`}
  style:--button-color-hover={`var(--color-${UseState}-secondary)`}
  style:--button-color-progress={`var(--color-${UseState}-accent)`}
  style:--button-hold-duration={`${HoldSeconds.toString()}s`}
  onclick={handleClick}
  onpointerdown={startHold}
  onpointerup={cancelHold}
  onpointerleave={handlePointerLeave}
  onkeydown={handleKeyDown}
  onkeyup={handleKeyUp}>
  <span class="buttonProgress" aria-hidden="true"></span>
  <span class="buttonLabel">
    {#if IsAsking}
      {Question}
    {:else}
      {@render children?.()}
      {#if NeedsHold}
        <small class="buttonHint">{HoldHint}</small>
      {/if}
    {/if}
  </span>
</svelte:element>

<style lang="scss">
  @use '../../../styles/display';

  .tagButton {
    @include display.inline-block;
    position: relative;
    overflow: hidden;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: calc(var(--font-size) * 16);
    padding: 0.6em 1em;
    font-size: var(--font-size);
    line-height: 1.25;
    text-align: center;
    cursor: pointer;
    border-radius: calc(var(--border-radius) / 2);
    border: 1px solid rgb(from var(--button-color) r g b / 45%);
    background-color: var(--button-color);
    color: var(--layout-font-color);

    &.alignLeft {
      justify-content: flex-start;
      text-align: left;

      .buttonLabel {
        align-items: flex-start;
      }
    }

    &:hover,
    &:focus-visible {
      background-color: var(--button-color-hover);
    }

    &.asking {
      background-color: var(--button-color-progress);
      font-style: italic;
    }

    .buttonProgress {
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      z-index: 0;
      width: 0;
      background-color: var(--button-color-progress);
      transition: width 0.15s linear;
    }

    &.holding .buttonProgress,
    &.held .buttonProgress {
      width: 100%;
    }

    &.holding .buttonProgress {
      transition-duration: var(--button-hold-duration);
    }

    .buttonLabel {
      position: relative;
      z-index: 1;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 0.25em;
    }

    .buttonHint {
      font-size: 0.75em;
      opacity: 0.8;
    }

    &.hinting {
      animation: buttonHintShake 0.3s ease-in-out;

      .buttonHint {
        font-weight: bold;
        opacity: 1;
      }
    }

    &[aria-disabled='true'] {
      cursor: default;

      &:hover,
      &:focus-visible {
        background-color: var(--button-color);
      }
    }
  }

  @keyframes buttonHintShake {
    0%,
    100% {
      transform: translateX(0);
    }
    25% {
      transform: translateX(-0.25em);
    }
    75% {
      transform: translateX(0.25em);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .tagButton.hinting {
      animation: none;
    }
  }
</style>
